import React, { useState, useEffect, useRef } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import useAuth from '../../utilities/hooks/useAuth'
import useConversations from '../../utilities/hooks/useConversations'
import useGroupConversations from '../../utilities/hooks/useGroupConversations'
import useUnreadMessages from '../../utilities/hooks/useUnreadMessages'
import { messagesChannelManager } from '../../channels/messages_channel'
import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import ConversationList from './ConversationList'
import ConversationView from './ConversationView'
import GroupConversationView from './GroupConversationView'
import NewConversationModal from './NewConversationModal'
import NewGroupConversationModal from './NewGroupConversationModal'
import DeleteGroupDialog from './DeleteGroupDialog'
import ReportDialog from './ReportDialog'
import BlockUserDialog from './BlockUserDialog'
import ArchiveDialog from './ArchiveDialog'

function MessagingCenter() {
  const { conversationId, groupConversationId } = useParams()
  const history = useHistory()
  const { currentUser } = useAuth()
  const { decrementBy } = useUnreadMessages()
  const {
    conversations,
    isLoading,
    fetchConversations,
    createConversation,
    archiveConversation,
    updateConversation,
    fetchArchivedConversations,
    unarchiveConversation,
    reorderConversation
  } = useConversations()

  const {
    groupConversations,
    isLoading: isLoadingGroups,
    createGroupConversation,
    updateGroupConversation,
    reorderGroupConversation,
    archiveGroupConversation,
    deleteGroupConversation
  } = useGroupConversations()

  const conversationsRef = useRef(conversations)
  const currentConversationIdRef = useRef(conversationId)
  const groupConversationsRef = useRef(groupConversations)
  const currentGroupConversationIdRef = useRef(groupConversationId)

  useEffect(() => {
    conversationsRef.current = conversations
  }, [conversations])

  useEffect(() => {
    currentConversationIdRef.current = conversationId
  }, [conversationId])

  useEffect(() => {
    groupConversationsRef.current = groupConversations
  }, [groupConversations])

  useEffect(() => {
    currentGroupConversationIdRef.current = groupConversationId
  }, [groupConversationId])

  const [showNewConversation, setShowNewConversation] = useState(false)
  const [showNewGroupConversation, setShowNewGroupConversation] = useState(false)
  const [showDeleteGroupDialog, setShowDeleteGroupDialog] = useState(false)
  const [deleteGroupId, setDeleteGroupId] = useState(null)
  const [deleteGroupName, setDeleteGroupName] = useState(null)
  const [showReportDialog, setShowReportDialog] = useState(false)
  const [showBlockDialog, setShowBlockDialog] = useState(false)
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [selectedGroupConversation, setSelectedGroupConversation] = useState(null)
  const [blockUserId, setBlockUserId] = useState(null)
  const [blockUserName, setBlockUserName] = useState(null)
  const [archiveConversationId, setArchiveConversationId] = useState(null)
  const [archiveUserName, setArchiveUserName] = useState(null)

  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const conversation = conversations.find((c) => c.id === conversationId)
      setSelectedConversation(conversation)
    } else if (!conversationId) {
      setSelectedConversation(null)
    }
  }, [conversationId, conversations])

  useEffect(() => {
    if (groupConversationId && groupConversations.length > 0) {
      const gc = groupConversations.find((c) => c.id === groupConversationId)
      setSelectedGroupConversation(gc)
    } else if (!groupConversationId) {
      setSelectedGroupConversation(null)
    }
  }, [groupConversationId, groupConversations])

  useEffect(() => {
    const handleChannelMessage = (data) => {
      if (data.type === 'new_message') {
        const msgConversationId = data.conversation_id?.toString()
        const currentConversation = conversationsRef.current.find((c) => c.id?.toString() === msgConversationId)
        const senderId = data.message?.attributes?.sender?.id || data.message?.sender?.id
        const isFromOther = senderId && currentUser && senderId.toString() !== currentUser.id.toString()
        const isViewingConversation = currentConversationIdRef.current?.toString() === msgConversationId

        const currentUnread = currentConversation?.attributes?.unreadCount || 0
        const newUnread = isFromOther && !isViewingConversation ? currentUnread + 1 : currentUnread

        updateConversation(msgConversationId, {
          attributes: {
            ...currentConversation?.attributes,
            lastMessageAt: new Date().toISOString(),
            lastMessagePreview:
              data.message?.attributes?.body?.substring(0, 100) || data.message?.body?.substring(0, 100),
            unreadCount: newUnread
          }
        })

        if (reorderConversation) {
          reorderConversation(msgConversationId)
        }
      }

      if (data.type === 'new_group_message') {
        const gcId = data.group_conversation_id?.toString()
        const currentGc = groupConversationsRef.current.find((gc) => gc.id?.toString() === gcId)
        const senderId = data.message?.attributes?.sender?.id
        const isFromOther = senderId && currentUser && senderId.toString() !== currentUser.id.toString()
        const isViewingGroup = currentGroupConversationIdRef.current?.toString() === gcId

        const currentUnread = currentGc?.attributes?.unreadCount || 0
        const newUnread = isFromOther && !isViewingGroup ? currentUnread + 1 : currentUnread

        updateGroupConversation(gcId, {
          attributes: {
            ...currentGc?.attributes,
            lastMessageAt: new Date().toISOString(),
            lastMessagePreview: data.message?.attributes?.body?.substring(0, 100),
            unreadCount: newUnread
          }
        })
        reorderGroupConversation(gcId)
      }
    }

    messagesChannelManager.addCallback(handleChannelMessage)

    return () => {
      messagesChannelManager.removeCallback(handleChannelMessage)
    }
  }, [updateConversation, reorderConversation, updateGroupConversation, reorderGroupConversation, currentUser])

  const handleSelectConversation = (id) => {
    const conversation = conversations.find((c) => c.id === id)
    const unreadCount = conversation?.attributes?.unreadCount || 0
    if (unreadCount > 0) {
      decrementBy(unreadCount)
      updateConversation(id, {
        ...conversation,
        attributes: {
          ...conversation.attributes,
          unreadCount: 0
        }
      })
    }
    history.push(`/messages/${id}`)
  }

  const handleBack = () => {
    history.push('/messages')
  }

  const handleCreateConversation = async (userId) => {
    const conversation = await createConversation(userId)
    history.push(`/messages/${conversation.id}`)
  }

  const handleSelectGroupConversation = (id) => {
    history.push(`/messages/g/${id}`)
  }

  const handleCreateGroupConversation = async (payload) => {
    const gc = await createGroupConversation(payload)
    history.push(`/messages/g/${gc.id}`)
  }

  const handleArchiveGroup = async (id) => {
    await archiveGroupConversation(id)
    history.push('/messages')
  }

  const handleDeleteGroup = (id, name) => {
    setDeleteGroupId(id)
    setDeleteGroupName(name)
    setShowDeleteGroupDialog(true)
  }

  const handleDeleteGroupConfirm = async () => {
    if (deleteGroupId) {
      await deleteGroupConversation(deleteGroupId)
      setShowDeleteGroupDialog(false)
      setDeleteGroupId(null)
      setDeleteGroupName(null)
      history.push('/messages')
    }
  }

  const handleArchive = (id, userName) => {
    setArchiveConversationId(id)
    setArchiveUserName(userName)
    setShowArchiveDialog(true)
  }

  const handleArchiveConfirm = async () => {
    if (archiveConversationId) {
      await archiveConversation(archiveConversationId)
      setShowArchiveDialog(false)
      setArchiveConversationId(null)
      setArchiveUserName(null)
      history.push('/messages')
    }
  }

  const handleBlock = (userId, userName) => {
    setBlockUserId(userId)
    setBlockUserName(userName)
    setShowBlockDialog(true)
  }

  const handleBlockConfirm = (success) => {
    setShowBlockDialog(false)
    if (success) {
      fetchConversations()
    }
  }

  const handleReport = () => {
    setShowReportDialog(true)
  }

  const handleMessagesRead = (readConversationId) => {
    const conversation = conversationsRef.current.find((c) => c.id === readConversationId)
    const unreadCount = conversation?.attributes?.unreadCount || 0
    if (unreadCount > 0) {
      decrementBy(unreadCount)
    }
    updateConversation(readConversationId, {
      ...conversation,
      attributes: {
        ...conversation?.attributes,
        unreadCount: 0
      }
    })
  }

  const isViewingGroup = !!groupConversationId
  const isViewingAny = !!conversationId || isViewingGroup

  return (
    <ApplicationLayout>
      <div className="flex h-full">
        <div
          className={`${isViewingAny ? 'hidden lg:block' : 'block'} w-full lg:w-96 h-full border-r border-gray-200`}
        >
          <ConversationList
            conversations={conversations}
            groupConversations={groupConversations}
            selectedConversationId={conversationId}
            selectedGroupConversationId={groupConversationId}
            onSelectConversation={handleSelectConversation}
            onSelectGroupConversation={handleSelectGroupConversation}
            onNewConversation={() => setShowNewConversation(true)}
            onNewGroupConversation={() => setShowNewGroupConversation(true)}
            isLoading={isLoading || isLoadingGroups}
            currentUser={currentUser}
            fetchArchivedConversations={fetchArchivedConversations}
            onRestoreConversation={unarchiveConversation}
          />
        </div>

        <div className={`${isViewingAny ? 'block' : 'hidden lg:block'} flex-1`}>
          {isViewingGroup ? (
            <GroupConversationView
              conversation={selectedGroupConversation}
              onBack={handleBack}
              onArchive={handleArchiveGroup}
              onDelete={handleDeleteGroup}
            />
          ) : (
            <ConversationView
              conversation={selectedConversation}
              onBack={handleBack}
              onArchive={(id) => handleArchive(id, selectedConversation?.attributes?.otherParticipant?.preferredName)}
              onBlock={(userId) => handleBlock(userId, selectedConversation?.attributes?.otherParticipant?.preferredName)}
              onReport={handleReport}
              onMessagesRead={handleMessagesRead}
            />
          )}
        </div>

        <NewConversationModal
          isOpen={showNewConversation}
          onClose={() => setShowNewConversation(false)}
          onCreateConversation={handleCreateConversation}
        />

        <NewGroupConversationModal
          isOpen={showNewGroupConversation}
          onClose={() => setShowNewGroupConversation(false)}
          onCreateGroupConversation={handleCreateGroupConversation}
        />

        <ReportDialog
          isOpen={showReportDialog}
          onClose={() => setShowReportDialog(false)}
          conversationId={conversationId}
        />

        <BlockUserDialog
          isOpen={showBlockDialog}
          onClose={handleBlockConfirm}
          userId={blockUserId}
          userName={blockUserName}
        />

        <ArchiveDialog
          isOpen={showArchiveDialog}
          onClose={() => setShowArchiveDialog(false)}
          onConfirm={handleArchiveConfirm}
          userName={archiveUserName}
        />

        <DeleteGroupDialog
          isOpen={showDeleteGroupDialog}
          onClose={() => setShowDeleteGroupDialog(false)}
          onConfirm={handleDeleteGroupConfirm}
          groupName={deleteGroupName}
        />
      </div>
    </ApplicationLayout>
  )
}

export default MessagingCenter
